import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useDropdowns } from "@/contexts/DropdownContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import UserSearchDropdown from "@/components/UserSearchDropdown";
import { SortableHeader, TablePagination } from "@/components/TableComponents";
import { useTableSort } from "@/hooks/useTableSort";
import { Plus, Bell, Edit, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TasksPage() {
  const { token, hasPermission, user } = useAuth();
  const { getOptions, getColor } = useDropdowns();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [formData, setFormData] = useState({
    title: "", priority: "Medium", due_date: "", assigned_to: "", assigned_to_id: "", status: "Pending", description: "",
    completion_percentage: "", delay_reason: "", remarks: "", next_day_task: ""
  });

  const isAdmin = user?.role === "Admin";

  // Get dropdown options
  const statusOptions = getOptions("tasks", "status");
  const priorityOptions = getOptions("tasks", "priority");

  useEffect(() => { 
    if (token) {
      fetchTasks();
      fetchUsersAndEmployees();
    }
  }, [token]);

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API}/tasks`, { headers: { Authorization: `Bearer ${token}` } });
      setTasks(res.data);
    } catch (e) {
      toast.error("Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersAndEmployees = async () => {
    try {
      const [usersRes, employeesRes] = await Promise.all([
        axios.get(`${API}/auth/users/assignable`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hr/employees`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setUsers(usersRes.data || []);
      setEmployees(employeesRes.data || []);
    } catch (e) {
      console.error("Failed to fetch users/employees");
    }
  };

  const handleAssigneeChange = (id, name) => {
    setFormData({
      ...formData,
      assigned_to_id: id,
      assigned_to: name
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        completion_percentage:
          formData.completion_percentage === "" || formData.completion_percentage === null
            ? null
            : Number(formData.completion_percentage),
      };
      if (editing) {
        await axios.put(`${API}/tasks/${editing.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Task updated");
      } else {
        await axios.post(`${API}/tasks`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Task created");
      }
      setDialogOpen(false);
      resetForm();
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await axios.delete(`${API}/tasks/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Task deleted");
      fetchTasks();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "", priority: "Medium", due_date: "", assigned_to: "", assigned_to_id: "", status: "Pending", description: "",
      completion_percentage: "", delay_reason: "", remarks: "", next_day_task: ""
    });
    setEditing(null);
  };

  const assignToSelf = () => {
    if (!user) return;
    setFormData((prev) => ({
      ...prev,
      assigned_to_id: user.id,
      assigned_to: user.name || user.email || "Me",
    }));
  };

  const openEdit = (task) => {
    setEditing(task);
    setFormData({
      title: task.title,
      priority: task.priority,
      due_date: task.due_date,
      assigned_to: task.assigned_to,
      assigned_to_id: task.assigned_to_id || "",
      status: task.status,
      description: task.description || "",
      completion_percentage:
        task.completion_percentage === null || task.completion_percentage === undefined
          ? ""
          : String(task.completion_percentage),
      delay_reason: task.delay_reason || "",
      remarks: task.remarks || "",
      next_day_task: task.next_day_task || "",
    });
    setDialogOpen(true);
  };

  const canWrite = hasPermission("tasks:write");
  const canDelete = hasPermission("tasks:delete");

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.assigned_to.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === "all" || task.status === filterStatus;
    const matchesPriority = filterPriority === "all" || task.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Sorting and pagination
  const {
    sortKey,
    sortOrder,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    startIndex,
    endIndex,
    paginatedData,
    totalItems
  } = useTableSort(filteredTasks, {
    defaultSortKey: "created_at",
    defaultSortOrder: "desc",
    defaultPageSize: 10
  });

  const hasActiveFilters = filterStatus !== "all" || filterPriority !== "all";
  const clearFilters = () => {
    setFilterStatus("all");
    setFilterPriority("all");
  };

  if (loading) return <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen text-slate-600 dark:text-slate-400">Loading...</div>;

  const statusColors = {
    "Pending": "bg-amber-50 text-amber-700 border-amber-200",
    "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
    "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Overdue": "bg-red-50 text-red-700 border-red-200"
  };

  const priorityColors = {
    "Low": "bg-slate-100 text-slate-700 border-slate-200",
    "Medium": "bg-amber-50 text-amber-700 border-amber-200",
    "High": "bg-red-50 text-red-700 border-red-200"
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="p-8 bg-slate-50 dark:bg-slate-900 min-h-screen">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Tasks & Reminders</h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Manage tasks, approvals, and escalations</p>
        </div>
        {canWrite && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-task-button" className="bg-brillien-600 hover:bg-brillien-700 text-white rounded-lg h-10 px-4 font-medium">
                <Plus className="h-4 w-4 mr-2" />Create Task
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-slate-800 dark:border-slate-700">
              <DialogHeader><DialogTitle>{editing ? "Edit Task" : "Create New Task"}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Title *</Label>
                  <Input 
                    value={formData.title} 
                    onChange={(e) => setFormData({...formData, title: e.target.value})} 
                    required 
                    className="rounded-lg" 
                    data-testid="task-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Priority *</Label>
                    <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                      <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {priorityOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Due Date *</Label>
                    <Input 
                      type="date" 
                      value={formData.due_date} 
                      onChange={(e) => setFormData({...formData, due_date: e.target.value})} 
                      required 
                      className="rounded-lg" 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between">
                      <Label>Assign To *</Label>
                      <button
                        type="button"
                        onClick={assignToSelf}
                        data-testid="assign-to-self-btn"
                        className="text-xs font-medium text-brillien-600 hover:text-brillien-700 hover:underline dark:text-brillien-400"
                      >
                        Assign to me
                      </button>
                    </div>
                    <UserSearchDropdown
                      users={users}
                      value={formData.assigned_to_id}
                      selectedLabel={formData.assigned_to}
                      onChange={handleAssigneeChange}
                      placeholder="Search by name or email..."
                    />
                  </div>
                  <div>
                    <Label>Status *</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                      <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {statusOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea 
                    value={formData.description} 
                    onChange={(e) => setFormData({...formData, description: e.target.value})} 
                    className="rounded-lg" 
                    rows={3} 
                  />
                </div>

                {/* Daily-update fields — for employees to log progress after check-in */}
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4 mt-2">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">
                    Daily Update
                    <span className="ml-2 text-xs font-normal text-slate-500 dark:text-slate-400">
                      (fill after your check-in)
                    </span>
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Completion %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        placeholder="0-100"
                        value={formData.completion_percentage}
                        onChange={(e) => setFormData({...formData, completion_percentage: e.target.value})}
                        className="rounded-lg"
                        data-testid="task-completion-percentage"
                      />
                    </div>
                    <div>
                      <Label>Delay Reason</Label>
                      <Input
                        value={formData.delay_reason}
                        onChange={(e) => setFormData({...formData, delay_reason: e.target.value})}
                        placeholder="If completion is delayed…"
                        className="rounded-lg"
                        data-testid="task-delay-reason"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label>Remarks</Label>
                    <Textarea
                      value={formData.remarks}
                      onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                      rows={2}
                      placeholder="Any additional notes about today's progress…"
                      className="rounded-lg"
                      data-testid="task-remarks"
                    />
                  </div>
                  <div className="mt-3">
                    <Label>Next Day Task / Tomorrow&apos;s Plan</Label>
                    <Textarea
                      value={formData.next_day_task}
                      onChange={(e) => setFormData({...formData, next_day_task: e.target.value})}
                      rows={2}
                      placeholder="What you plan to work on next…"
                      className="rounded-lg"
                      data-testid="task-next-day-task"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button>
                  <Button type="submit" data-testid="task-submit-button" className="bg-brillien-600 hover:bg-brillien-700 rounded-lg">
                    {editing ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search and Filters */}
      <Card className="p-4 border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg mb-6">
        <div className="flex flex-col gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search tasks by title, ID, assigned person, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="tasks-search-input"
              className="pl-10 h-10 rounded-lg border-slate-300 dark:border-slate-400 dark:bg-slate-700 dark:text-white"
            />
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Filters:</span>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36 h-9 rounded-lg" data-testid="filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statusOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-36 h-9 rounded-lg" data-testid="filter-priority">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priority</SelectItem>
                {priorityOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 dark:text-slate-400 h-9">
                Clear All
              </Button>
            )}
            <span className="text-sm text-slate-500 dark:text-slate-400 ml-auto">
              {filteredTasks.length} of {tasks.length} tasks
            </span>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-700/50">
              <TableHead>
                <SortableHeader sortKey="id" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Task ID
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="title" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Title
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="assigned_to" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Assigned To
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="due_date" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Due Date
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="priority" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Priority
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="status" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Status
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="completion_percentage" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Completion %
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="created_at" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Created
                </SortableHeader>
              </TableHead>
              <TableHead>
                <SortableHeader sortKey="updated_at" currentSortKey={sortKey} sortOrder={sortOrder} onSort={handleSort}>
                  Updated
                </SortableHeader>
              </TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedData.map((task) => (
              <TableRow key={task.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50" data-testid={`task-row-${task.id}`}>
                <TableCell className="font-mono text-sm text-slate-600 dark:text-slate-400">{task.id}</TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{task.title}</p>
                    {task.description && <p className="text-sm text-slate-500 dark:text-slate-400 truncate max-w-xs">{task.description}</p>}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-brillien-700 dark:text-brillien-400 font-medium">{task.assigned_to}</TableCell>
                <TableCell className="text-sm text-slate-700 dark:text-slate-300">{task.due_date}</TableCell>
                <TableCell>
                  <Badge className={`${getColor("tasks", "priority", task.priority)} border text-xs`}>{task.priority}</Badge>
                </TableCell>
                <TableCell>
                  <Badge className={`${getColor("tasks", "status", task.status)} border text-xs`}>{task.status}</Badge>
                </TableCell>
                <TableCell className="text-sm text-slate-700 dark:text-slate-300 min-w-[110px]" data-testid={`task-completion-${task.id}`}>
                  {task.completion_percentage === null || task.completion_percentage === undefined ? (
                    <span className="text-slate-400">—</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                        <div
                          className="h-full bg-brillien-600"
                          style={{ width: `${Math.max(0, Math.min(100, task.completion_percentage))}%` }}
                        />
                      </div>
                      <span className="text-xs font-mono w-9 text-right">{task.completion_percentage}%</span>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm text-slate-600 dark:text-slate-400">{formatDate(task.created_at)}</TableCell>
                <TableCell className="text-sm text-slate-600 dark:text-slate-400">{formatDate(task.updated_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {canWrite && (
                      <Button variant="ghost" size="sm" onClick={() => openEdit(task)} className="h-8 w-8 p-0">
                        <Edit className="h-4 w-4 text-slate-600" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(task.id)} className="h-8 w-8 p-0">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {paginatedData.length === 0 && (
          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
            <Bell className="h-12 w-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
            <p>No tasks found</p>
            {canWrite && <p className="text-sm mt-1">Click &quot;Create Task&quot; to add one</p>}
          </div>
        )}
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          startIndex={startIndex}
          endIndex={endIndex}
          totalItems={totalItems}
        />
      </Card>
    </div>
  );
}
